/**
 * Vue Router — dashboard routes + auth gating.
 *
 * Dashboard routes (/, /branches/:id, /historical) require AuthState='approved'.
 * Public routes (/login, /pending, /revoked) bypass the guard.
 *
 * bead: lucky-war-hw7.1
 */
import { createRouter, createWebHistory } from 'vue-router';

import HomeView from '@/views/HomeView.vue';
import { useAuth, AuthState, bootstrapAuth } from '@/composables/useAuth';

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    // ── Auth views (public — no auth required) ─────────────────────────────
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/LoginView.vue'),
    },
    {
      path: '/pending',
      name: 'pending',
      component: () => import('@/views/PendingView.vue'),
    },
    {
      path: '/revoked',
      name: 'revoked',
      component: () => import('@/views/RevokedView.vue'),
    },

    // ── Dashboard (protected) ──────────────────────────────────────────────
    { path: '/', name: 'home', component: HomeView },
    {
      path: '/branch',
      name: 'branch-list',
      component: () => import('@/views/BranchListView.vue'),
    },
    {
      // Branch-vs-branch side-by-side comparison (bead lucky-war-1t4r.2).
      // Selection is shareable via ?ids=A,B,C.
      path: '/compare',
      name: 'compare',
      component: () => import('@/views/CompareView.vue'),
    },
    {
      path: '/branches/:id',
      name: 'branch',
      component: () => import('@/views/BranchView.vue'),
      props: true,
    },
    {
      path: '/weekly/:weekId',
      name: 'weekly-week',
      component: () => import('@/views/WeeklyView.vue'),
      props: true,
    },
    {
      path: '/weekly',
      name: 'weekly',
      component: () => import('@/views/WeeklyView.vue'),
    },
    {
      path: '/historical',
      name: 'historical',
      component: () => import('@/views/HistoricalView.vue'),
    },
    {
      // Frontend-only prototype; intentionally hidden from AppNav.
      path: '/historical-demo',
      name: 'historical-demo',
      component: () => import('@/views/HistoricalDemoView.vue'),
    },
    {
      path: '/discovery',
      name: 'discovery',
      component: () => import('@/views/DiscoveryView.vue'),
    },
    {
      path: '/competitive-analysis',
      name: 'competitive-analysis',
      component: () => import('@/views/CompetitiveAnalysisView.vue'),
    },
    {
      path: '/social',
      name: 'social',
      component: () => import('@/views/SocialView.vue'),
    },

    // ── Maintainer ops (protected — any approved viewer; bead lucky-war-jjn.9.5)
    {
      path: '/ops',
      name: 'ops',
      component: () => import('@/views/OpsView.vue'),
    },

    // ── Admin — promo↔content mapper (role=admin only; bead lucky-war-h6zm).
    // Attribution-affecting writes: stricter than /ops's approved-viewer gate.
    {
      path: '/admin/promotions',
      name: 'admin-promotions',
      component: () => import('@/views/PromoAdminView.vue'),
      meta: { requiresAdmin: true },
    },

    // Legacy alert links now recover inside the authenticated operations page.
    {
      path: '/admin/alerts',
      name: 'admin-alerts-legacy',
      redirect: { name: 'ops', query: { notice: 'alerts-moved' } },
    },

    // Catch-all keeps the SPA in sync for any deep link.
    { path: '/:pathMatch(.*)*', redirect: '/' },
  ],
});

// ── Navigation guard ──────────────────────────────────────────────────────────
// Runs after the auth bootstrap resolves (App.vue withholds RouterView until
// state !== Loading). Routes that arrive while loading are held by the splash.

const PUBLIC_ROUTES = new Set(['login', 'pending', 'revoked']);

router.beforeEach(async (to, _from) => {
  const { state, role } = useAuth();

  // Public routes: always allowed, regardless of auth state.
  if (PUBLIC_ROUTES.has(to.name as string)) {
    return true;
  }

  // If the auth bootstrap hasn't resolved yet, wait for it. Returning false
  // here would CANCEL the initial navigation; the router would then have no
  // current route when App.vue unhooks the splash, leaving the page blank.
  // Awaiting lets the guard re-evaluate with the resolved state below.
  if (state.value === AuthState.Loading) {
    await bootstrapAuth();
  }

  // Dashboard routes: gate by auth state.
  switch (state.value) {
    case AuthState.Approved:
      // Admin-only routes (e.g. /admin/promotions): redirect non-admins home
      // instead of showing a 403 wall — the nav doesn't advertise the route.
      if (to.meta.requiresAdmin && role.value !== 'admin') {
        return { name: 'home' };
      }
      return true;

    case AuthState.Unauthenticated:
      return { name: 'login' };

    case AuthState.Pending:
      return { name: 'pending' };

    case AuthState.Revoked:
      return { name: 'revoked' };

    default:
      return { name: 'login' };
  }
});

export default router;
